import type { Api } from '@rialtic/api'
import type { AuthHeaders } from '@rialtic/types'
import type { AppType } from '@worker/auth0-edge-access'
import { hc } from 'hono/client'
import { useWorkspace } from '~/stores/workspace'
import { makeTracingHeaders } from '#imports'

export const useEdgeAccessClient = (token?: string) => {
  const config = useRuntimeConfig().public

  const { $auth } = useNuxtApp()
  const workspace = useWorkspace()

  return hc<AppType>(<string>config.AUTH0_ACCESS_API_BASE_URL, {
    headers: {
      Authorization: `Bearer ${token || $auth.accessToken}`,
      'auth-provider': !$auth.accessToken ? 'auth0' : 'cognito',
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      workspace: workspace.workspaceId,
    },
  })
}

export const useRpcClient = (token?: string) => {
  const config = useRuntimeConfig().public

  const { $auth } = useNuxtApp()
  const workspace = useWorkspace()

  return hc<Api.AppType>(<string>config.WORKERS_API_BASE_URL, {
    headers: {
      Authorization: `Bearer ${token || $auth.accessToken}`,
      'auth-provider': !$auth.accessToken ? 'auth0' : 'cognito',
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      'x-rialtic-impact-calculation-method':
        workspace.financialCalculationMethod,
      workspace: workspace.workspaceId,
    },
  })
}

export const useWorkersApi = () => {
  const config = useRuntimeConfig().public
  const { $auth, $auth0, $datadog } = useNuxtApp()
  const workspace = useWorkspace()

  const baseURL = <string>config.WORKERS_API_BASE_URL

  const getAuthHeaders = async (): Promise<AuthHeaders | undefined> => {
    let token = $auth.accessToken

    const { isAuthenticated, getAccessTokenSilently } = $auth0()

    let useAuth0Token = false

    if (!$auth.refreshToken && isAuthenticated.value) {
      try {
        token = await getAccessTokenSilently()

        useAuth0Token = !!token
      } catch (_) {}
    }

    if (!token && $auth.refreshToken) {
      try {
        token = await $auth.checkAndRefresh()
      } catch (_) {
        return
      }
    }

    return {
      Authorization: `Bearer ${token}`,
      'auth-provider': useAuth0Token ? 'auth0' : 'cognito',
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      workspace: workspace.workspaceId,
      ...makeTracingHeaders(),
    }
  }

  const client = $fetch.create({
    baseURL,
    async onRequest({ options }) {
      const authHeaders = await getAuthHeaders()
      if (!authHeaders) {
        return
      }
      options.headers = {
        ...options.headers,
        ...authHeaders,
        ...makeTracingHeaders(),
      }
    },
    async onResponseError({
      error,
      response,
      options: { baseURL, params, query },
    }) {
      if (response.status === 401) {
        try {
          await $auth.checkAndRefresh()
        } catch (_) {
          return
        }
      } else if (response.status === 400) {
        $datadog.addAction('fetch_400', { baseURL, error, params, query })
      } else {
        $datadog.addError(error, { baseURL, params, query })
      }
    },
  })

  const fetchPublic = $fetch.create({
    baseURL,

    async onRequest({ options }) {
      options.headers = {
        ...options.headers,
        ...makeTracingHeaders(),
      }
    },

    async onResponseError({
      error,
      response,
      options: { baseURL, params, query },
    }) {
      if (response.status === 400) {
        $datadog.addAction('fetch_400', { baseURL, error, params, query })
      } else {
        $datadog.addError(error, { baseURL, params, query })
      }
    },
  })

  return {
    baseURL,
    fetch: client,
    fetchPublic,
    getAuthHeaders,
  }
}
